import type { RoleCode } from '../../api/contrat.js';
import { valeurLegaleValide, type CleLegale, type FormeValeur } from '../../../../../packages/contracts/src/legal/index.js';
import type { Etude, EtudeSaisie, ParametreLegal, ParametreSaisie } from './types.js';

/** Ce que les règles lisent d'un utilisateur ; un `Utilisateur` du contrat convient. */
export interface PersonneLue {
  id: string;
  kind: string;
  roles: readonly { role: string; scopeType: string }[];
}

/**
 * Règles pures de l'écran Administration (D10). Aucun seuil légal n'est écrit ici : valeurs, bornes et libellés
 * viennent du contrat et des données versionnées.
 */

const LIBELLES_ROLE: Readonly<Record<RoleCode, string>> = {
  admin: 'administrateur',
  dirigeante: 'dirigeante',
  assistante: 'assistante',
  agent: 'agent',
  clerc: 'clerc',
  notaire: 'notaire',
  mandant: 'mandant',
  heritier: 'héritier',
  prestataire: 'prestataire',
  representant_legal: 'représentant légal',
  contact_delegue: 'contact délégué',
};

const premierRole = (u: PersonneLue): string | undefined => u.roles[0]?.role;
const estAdmin = (u: PersonneLue): boolean => u.roles.some((r) => r.role === 'admin');

export const libelleRole = (role: string): string => LIBELLES_ROLE[role as RoleCode] ?? role;

export function libellePortee(u: PersonneLue): string {
  const role = libelleRole(premierRole(u) ?? 'inconnu');
  if (u.kind === 'portal') return `${role} · espace client`;
  const portee = u.roles[0]?.scopeType;
  return `${role} · ${portee === 'etude' ? 'étude' : portee === 'dossier' ? 'dossier' : portee === 'mission' ? 'mission' : 'toute l’équipe'}`;
}

export function initiales(nom: string): string {
  return nom
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((m) => (m[0] ?? '').toUpperCase())
    .join('');
}

export type VerdictIncarnation = { ok: true } | { ok: false; raison: string };

/** Seuls les administrateurs incarnent ; jamais soi-même, jamais un administrateur, jamais en imbrication (T-R-VOIR-05). */
export function verifierIncarnation(acteur: PersonneLue, cible: PersonneLue, dejaEnCours = false): VerdictIncarnation {
  if (!estAdmin(acteur)) return { ok: false, raison: 'Seul un administrateur peut voir Lakou en tant qu’une autre personne.' };
  if (dejaEnCours) return { ok: false, raison: 'Terminez la session en cours avant d’en commencer une autre.' };
  if (acteur.id === cible.id) return { ok: false, raison: 'Vous ne pouvez pas vous incarner vous-même.' };
  if (estAdmin(cible)) return { ok: false, raison: 'Un compte administrateur ne peut pas être incarné.' };
  return { ok: true };
}

export const motifValide = (motif: string): boolean => motif.trim().length >= 3;

export type EvaluationAction = { type: 'libre' } | { type: 'confirmation-requise' } | { type: 'refusee'; raison: string };

/** CA-ADM-01b : toute action en tant que demande une confirmation explicite ; la lecture seule n'en permet aucune. */
export function evaluerAction(mode: 'read' | 'act' | null): EvaluationAction {
  if (mode === null) return { type: 'libre' };
  if (mode === 'read') return { type: 'refusee', raison: 'Cette vue est en lecture seule : aucune action n’est possible.' };
  return { type: 'confirmation-requise' };
}

/** CA-ADM-02a : une étude qui a des personnes rattachées exige la réaffectation des dossiers avant suppression. */
export function avertissementSuppression(etude: Etude): string | null {
  const n = etude.members.length;
  if (n === 0) return null;
  return `Cette étude compte ${n} ${n > 1 ? 'personnes rattachées' : 'personne rattachée'}. Réaffectez ses dossiers avant de la supprimer.`;
}

export const resumeEtude = (etude: Etude): string =>
  etude.members.length === 0 ? 'à renseigner' : etude.members.map((m) => `${m.displayName}, ${m.function}`).join(' · ');

/** Nombre d'accès (personnes rattachées) d'une étude : une ligne = un accès. */
export const libelleAcces = (n: number): string => (n === 0 ? 'aucun accès' : `${n} accès`);

/** Valeur en vigueur à une date (T-R-PARAM-02) : la plus récente dont la période couvre la date. */
export function valeurEnVigueur(liste: readonly ParametreLegal[], cle: string, date: string): ParametreLegal | null {
  const candidates = liste
    .filter((p) => p.key === cle && p.effectiveFrom <= date && (p.effectiveTo === null || p.effectiveTo >= date))
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
  return candidates[0] ?? null;
}

const UNITES: Partial<Record<FormeValeur, [string, string]>> = {
  annees: ['an', 'ans'],
  mois: ['mois', 'mois'],
  jours: ['jour', 'jours'],
  secondes: ['seconde', 'secondes'],
};

const nombreFr = (n: number): string => String(n).replace('.', ',');

export function libelleValeur(forme: FormeValeur | string, valeur: unknown, effectiveTo: string | null): string {
  let base: string;
  if (typeof valeur === 'number') {
    const unite = UNITES[forme as FormeValeur];
    if (unite) base = `${nombreFr(valeur)} ${valeur > 1 ? unite[1] : unite[0]}`;
    else if (forme === 'pourcent') base = `${nombreFr(valeur)} %`;
    else base = nombreFr(valeur);
  } else if (typeof valeur === 'string') {
    base = valeur;
  } else {
    base = 'valeur détaillée';
  }
  return effectiveTo ? `${base} · jusqu’en ${effectiveTo.slice(0, 4)}` : base;
}

const FORMES_SIMPLES: readonly string[] = ['mois', 'annees', 'jours', 'pourcent', 'entier', 'secondes'];
export const formeModifiable = (forme: string): boolean => FORMES_SIMPLES.includes(forme);

export const saisieValide = (cle: CleLegale, valeur: unknown): boolean => valeurLegaleValide(cle, valeur);

export const STATUTS_PARAMETRE: Readonly<Record<string, string>> = {
  verifie: 'vérifié',
  a_valider: 'à valider',
  decide: 'décidé',
  propose: 'proposé',
};

export type LireChamp = (nom: string) => string;

/** Saisie du formulaire « Ajouter une étude » : les champs facultatifs vides deviennent `null`. */
export const saisieEtude = (lire: LireChamp): EtudeSaisie => ({
  name: lire('nom').trim(),
  address: lire('adresse').trim() || null,
  notaireTitulaire: lire('notaire').trim() || null,
});

/** Saisie du formulaire « nouvelle valeur datée » : la période court de la date d'effet, sans fin connue. */
export const saisieSeuil = (lire: LireChamp): ParametreSaisie => ({
  key: lire('cle') as ParametreSaisie['key'],
  value: Number(lire('valeur')),
  effectiveFrom: lire('effet'),
  effectiveTo: null,
  sourceUrl: null,
  note: lire('note').trim() || null,
});

export const formaterInstant = (instant: string): string =>
  new Date(instant).toLocaleString('fr-FR', { timeZone: 'America/Guadeloupe', dateStyle: 'short', timeStyle: 'short' });

export function dureeMinutes(debut: string, fin: string | null): string {
  if (!fin) return 'en cours';
  const minutes = Math.max(0, Math.round((Date.parse(fin) - Date.parse(debut)) / 60000));
  return `${minutes} min`;
}
